import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { adminGet } from './adminApi';
import PlannedStopsCard from './PlannedStopsCard';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const GREEN = '#0B6B55';
const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const dotIcon = L.divIcon({
  className: '', iconSize: [16, 16], iconAnchor: [8, 8],
  html: `<div style="width:16px;height:16px;border-radius:50%;background:${GREEN};border:3px solid white;box-shadow:0 0 0 2px ${GREEN}"></div>`,
});

function TripLiveMap({ tripId, isOpen }) {
  const [track, setTrack] = useState(null);
  useEffect(() => {
    adminGet(`/trips/${tripId}/track`).then(setTrack).catch(() => setTrack({ points: [], denied: true }));
    if (!isOpen) return;
    const t = setInterval(() => adminGet(`/trips/${tripId}/track`).then(setTrack).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [tripId, isOpen]);

  if (!track) return null;
  if (track.denied) return null;
  if (!track.points.length) {
    return (
      <div className="rounded-lg border bg-white p-5 mb-6" style={{ borderColor: '#E5E3DC' }} data-testid="trip-live-map-empty">
        <h2 className="font-bold text-lg mb-1" style={{ color: '#1B1D1C' }}>Live position</h2>
        <p className="text-sm" style={{ color: '#9a9e96' }}>No GPS points received from the device yet. Owner-only view.</p>
      </div>
    );
  }
  const pts = track.points.map(p => [p.lat, p.lon]);
  const last = track.points[track.points.length - 1];
  return (
    <div className="rounded-lg border bg-white p-5 mb-6" style={{ borderColor: '#E5E3DC' }} data-testid="trip-live-map">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-lg" style={{ color: '#1B1D1C' }}>Live position
          {track.live && <span className="ml-2 px-2 py-0.5 rounded-full text-xs align-middle" style={{ background: '#E7F2EE', color: GREEN }}>live · refreshes every 30 s</span>}</h2>
        <span className="text-xs" style={{ color: '#9a9e96' }}>Owner-only · {track.points.length} points</span>
      </div>
      <div className="rounded-md overflow-hidden" style={{ height: 320 }}>
        <MapContainer center={pts[pts.length - 1]} zoom={12} style={{ height: '100%', width: '100%' }}>
          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors' />
          <Polyline positions={pts} pathOptions={{ color: GREEN, weight: 3 }} />
          <Marker position={pts[pts.length - 1]} icon={dotIcon}>
            <Popup>Last seen {new Date(last.t).toLocaleString()}{last.temp != null ? ` · ${last.temp} °C` : ''}</Popup>
          </Marker>
        </MapContainer>
      </div>
    </div>
  );
}

function TripDetail({ tripId }) {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { adminGet(`/trips/${tripId}`).then(setD).catch(e => toast.error(e.message)); }, [tripId]);
  if (!d) return <p className="text-sm p-8" style={{ color: '#9a9e96' }}>Loading…</p>;

  const stBadge = d.status === 'OPEN'
    ? { background: '#E7F2EE', color: GREEN } : { background: '#EFEDE6', color: '#6b7069' };
  return (
    <div className="flex" data-testid="trip-detail-page">
      <div className="flex-1 p-8 min-w-0">
        <button data-testid="back-to-trips" onClick={() => navigate('/admin/trips')}
          className="flex items-center gap-1.5 text-sm mb-3 hover:underline" style={{ color: '#6b7069' }}>
          <ArrowLeft size={15} /> Trips</button>
        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-4xl font-bold" style={{ ...mono, color: '#1B1D1C' }}>{d.code}</h1>
          <span className="px-2 py-0.5 rounded text-sm" style={stBadge}>{d.status === 'OPEN' ? 'In transit' : 'Ended'}</span>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E5E3DC' }}>
            <p className="text-xs" style={{ color: '#6b7069' }}>1 · Device QR scanned</p>
            <p className="font-bold" style={mono}>{d.track0tic_id || '—'}</p>
            <p className="text-xs" style={{ color: '#9a9e96' }}>by {d.started_by || '—'} · {d.started_at ? new Date(d.started_at).toLocaleString() : ''}</p>
          </div>
          <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E5E3DC' }}>
            <p className="text-xs" style={{ color: '#6b7069' }}>2 · Passport QRs scanned</p>
            <p className="font-bold">{d.passports.length} labels · {new Set(d.passports.map(p => p.invoice_no).filter(Boolean)).size || '—'} invoices</p>
            <p className="text-xs" style={{ color: '#9a9e96' }}>
              {d.passports[0]?.short_code}{d.passports.length > 1 ? ` to ${d.passports[d.passports.length - 1].short_code}` : ''}</p>
          </div>
          <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E5E3DC' }}>
            <p className="text-xs" style={{ color: '#6b7069' }}>3 · Confirmed once</p>
            <p className="font-bold">All {d.passports.length} Dormant → Active</p>
            <p className="text-xs" style={{ color: '#9a9e96' }}>one action</p>
          </div>
        </div>
        <TripLiveMap tripId={tripId} isOpen={d.status === 'OPEN'} />
        <PlannedStopsCard tripId={tripId} tenantId={d.tenant_id} />
        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }}>
          <h2 className="font-bold text-lg mb-3" style={{ color: '#1B1D1C' }}>Passports on this trip</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
              <th className="py-2 font-medium">Label</th><th className="font-medium">Invoice</th>
              <th className="font-medium">Delivery point</th><th className="font-medium">Device (telemetry source)</th>
              <th className="font-medium">Status</th><th className="font-medium">Certificate</th></tr></thead>
            <tbody>
              {d.passports.map(p => (
                <tr key={p.passport_id} className="border-b cursor-pointer hover:bg-[#FAF9F6]" style={{ borderColor: '#F3F1EA' }}
                  data-testid={`trip-passport-${p.short_code}`}
                  onClick={() => navigate(`/admin/passports/${p.passport_id}`)}>
                  <td className="py-3" style={mono}>{p.short_code}</td>
                  <td style={mono}>{p.invoice_no || '—'}</td>
                  <td>{p.receiver || '—'}</td>
                  <td style={mono}>{p.current_device || '—'}</td>
                  <td><span className="px-2 py-0.5 rounded text-xs" style={
                    p.state === 'CLOSED' ? { background: '#EFEDE6', color: '#6b7069' }
                      : p.state === 'ACTIVE' ? { background: '#E7F2EE', color: GREEN }
                        : { background: '#FBEBEB', color: '#8C2B2B' }}>
                    {p.state === 'CLOSED' ? 'Closed · delivered' : p.state === 'ACTIVE' ? 'Active · in transit' : p.state}</span></td>
                  <td>{p.certificate_url
                    ? <a href={`${API_BASE}${p.certificate_url}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      className="underline" style={{ color: GREEN }} data-testid={`cert-link-${p.short_code}`}>PDF</a>
                    : <span style={{ color: '#9a9e96' }}>{p.state === 'ACTIVE' ? 'On delivery' : '—'}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>
            Each passport's certificate uses only the telemetry from its own active window.</p>
        </div>
      </div>
      <div className="w-80 shrink-0 border-l p-6" style={{ borderColor: '#E5E3DC' }}>
        <h2 className="font-bold text-lg mb-3" style={{ color: '#1B1D1C' }}>Re-binding history</h2>
        {d.rebinds.length === 0 && <p className="text-sm" style={{ color: '#9a9e96' }}>No re-binds on this trip</p>}
        {d.rebinds.map((r, i) => (
          <div key={`${r.short_code}-${r.at || i}`} className="rounded-lg border bg-white p-4 mb-3" style={{ borderColor: '#E5E3DC' }} data-testid={`rebind-${i}`}>
            <p className="font-bold text-sm" style={mono}>{r.short_code}</p>
            <p className="text-sm mt-1">Moved from <span style={mono}>{r.from_device || 'no device'}</span> to <span style={mono}>{r.to_device || 'no device'}</span></p>
            <p className="text-xs mt-1" style={{ color: '#9a9e96' }}>{r.by} · {r.at ? new Date(r.at).toLocaleString() : ''}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminTripsPage() {
  const { tripId } = useParams();
  const [trips, setTrips] = useState([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (tripId) return;
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (q) qs.set('q', q);
    adminGet(`/trips?${qs}`).then(setTrips).catch(e => toast.error(e.message));
  }, [tripId, status, q]);

  if (tripId) return <TripDetail tripId={tripId} />;

  return (
    <div className="p-8" data-testid="admin-trips-page">
      <h1 className="text-4xl font-bold mb-5" style={{ color: '#1B1D1C' }}>Trips</h1>
      <div className="flex gap-3 mb-4">
        <input data-testid="trips-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search trip code or device…"
          className="h-9 px-3 rounded-md border bg-white text-sm w-72" style={{ borderColor: '#D9D6CD' }} />
        <select data-testid="trips-status-filter" value={status} onChange={e => setStatus(e.target.value)}
          className="h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
          <option value="">All</option><option value="OPEN">Open</option><option value="ENDED">Ended</option>
        </select>
      </div>
      <div className="rounded-lg border bg-white" style={{ borderColor: '#E5E3DC' }}>
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
            <th className="py-3 px-4 font-medium">Trip</th><th className="font-medium">Device</th>
            <th className="font-medium">Passports</th><th className="font-medium">Started by</th>
            <th className="font-medium">Started</th><th className="font-medium pr-4">Status</th></tr></thead>
          <tbody>
            {trips.map(t => (
              <tr key={t.id} data-testid={`trip-row-${t.code}`} className="border-b cursor-pointer hover:bg-[#FAF9F6]"
                style={{ borderColor: '#F3F1EA' }} onClick={() => navigate(`/admin/trips/${t.id}`)}>
                <td className="py-3 px-4" style={mono}>{t.code}</td>
                <td style={mono}>{t.track0tic_id || '—'}</td>
                <td style={mono}>{t.passports_count}</td>
                <td>{t.started_by || '—'}</td>
                <td>{t.started_at ? new Date(t.started_at).toLocaleString() : '—'}</td>
                <td className="pr-4"><span className="px-2 py-0.5 rounded text-xs" style={
                  t.status === 'OPEN' ? { background: '#E7F2EE', color: GREEN } : { background: '#EFEDE6', color: '#6b7069' }}>
                  {t.status === 'OPEN' ? 'In transit' : 'Ended'}</span></td>
              </tr>
            ))}
            {trips.length === 0 && <tr><td colSpan={6} className="py-8 text-center" style={{ color: '#9a9e96' }}>No trips yet — start one from the Scan page</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
